import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../auth/AuthProvider';
import SphereOrb from './SphereOrb';
import SocialAuth from './SocialAuth';
import '../Dashboard.css';

// Email/password sign-in. Accounts can be created in the app or on /register (web) —
// in-person friend verification still only happens in the app.
function messageFor(code) {
  switch (code) {
    case 'auth/invalid-email': return 'That email doesn’t look right.';
    case 'auth/missing-email': return 'Enter your email first.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential': return 'Email or password is incorrect.';
    case 'auth/too-many-requests': return 'Too many attempts. Try again in a bit.';
    case 'auth/network-request-failed': return 'Network error. Check your connection.';
    default: return 'Could not sign in. Please try again.';
  }
}

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = location.state?.from?.pathname || '/app';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [unverified, setUnverified] = useState(false);

  // Already signed in AND verified — skip the form. (An unverified session is held here so
  // the user can resend the link; the backend gate blocks its API calls regardless.) Runs as an
  // effect, not during render, so we never call navigate() mid-render.
  useEffect(() => {
    if (!loading && user && user.emailVerified) navigate(dest, { replace: true });
  }, [loading, user, dest, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null); setUnverified(false);
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      if (!cred.user.emailVerified) {
        // Hold them here (don't navigate) and offer a resend. Session stays alive so
        // sendEmailVerification has a signed-in user to act on.
        setUnverified(true);
        return;
      }
      navigate(dest, { replace: true });
    } catch (err) {
      setError(messageFor(err?.code));
    } finally {
      setBusy(false);
    }
  };

  const onForgot = async () => {
    setError(null); setNotice(null);
    const target = email.trim();
    if (!target) { setError('Enter your email first, then tap “Forgot password”.'); return; }
    try {
      await sendPasswordResetEmail(auth, target);
      setNotice(`Password reset link sent to ${target}. Check your inbox.`);
    } catch (err) {
      setError(messageFor(err?.code));
    }
  };

  const onResendVerification = async () => {
    setError(null); setNotice(null);
    try {
      if (auth.currentUser) await sendEmailVerification(auth.currentUser);
      setNotice(`Verification link sent to ${auth.currentUser?.email || email}. Tap it, then sign in.`);
    } catch (err) {
      setError(messageFor(err?.code));
    }
  };

  return (
    <div className="db-auth-page">
      <div className="db-auth-bg" aria-hidden="true">
        <span className="db-glow db-glow-a" />
        <span className="db-glow db-glow-b" />
      </div>
      <form className="db-auth-card" onSubmit={onSubmit}>
        <div className="db-auth-hero">
          <SphereOrb size={120} />
          <Link to="/" className="db-auth-brand">Sphere<span className="db-grad">Sync</span></Link>
          <p className="db-auth-tag">People. Places. Moments. In sync.</p>
        </div>
        <h1 className="db-auth-title">Welcome back</h1>
        <p className="db-auth-sub">Sign in to see your circles, moments, and badges.</p>

        <label className="db-field">
          <span>Email</span>
          <input
            type="email" autoComplete="email" required
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </label>
        <label className="db-field">
          <span>Password</span>
          <input
            type="password" autoComplete="current-password" required
            value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />
        </label>

        <button type="button" className="db-auth-link" onClick={onForgot}>Forgot password?</button>

        {error && <div className="db-auth-error">{error}</div>}
        {notice && <div className="db-auth-notice">{notice}</div>}
        {unverified && (
          <div className="db-auth-notice">
            Your email isn’t verified yet. Check your inbox for the link, then sign in.
            <button type="button" className="db-auth-link" onClick={onResendVerification}>Resend verification email</button>
          </div>
        )}

        <button className="db-btn db-btn-primary db-btn-block" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <SocialAuth dest={dest} onError={setError} />

        <p className="db-auth-foot">
          No account yet?{' '}
          <Link to="/register" state={location.state} className="db-auth-link">Create one</Link>
          {' '}— in-person friend verification happens in the app.
        </p>
      </form>
    </div>
  );
}
